<template>
  <div class="__ForecastChartBar">
    <!-- Chart -->
    <div :id="`${name}-div`" :ref="`${name}-div`" :style="{ width: '100%', height: height }" />

    <!-- Slot - Foot content -->
    <slot name="foot-content" />
  </div>
</template>

<script>
/*
 * [Events]
 *
 * [Methods]
 * generateChart() => void 0
 */

import Vue from 'vue';
import { mapGetters } from 'vuex';

import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import * as chartFn from '@/components/CustomCharts/chartFn';
import chartColors from '@/components/CustomCharts/colors';

// Chart common lang
import chartLang from '@/components/CustomCharts/lang';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const messages = {
  en: {
    // Chart common lang
    ...chartLang.en,

    'Cost ({symbol})': 'Cost ({symbol})',
  },
  ja: {
    // Chart common lang
    ...chartLang.ja,

    'Cost ({symbol})': 'コスト（{symbol}）',
  },
};

export default Vue.extend({
  name: 'ForecastChartBar',
  i18n: { messages },
  components: {},
  props: {
    name: {
      type: String,
      default: 'chart',
    },

    data: {
      type: Array,
      required: true,
    },

    series: {
      type: Array,
      required: true,
    },

    gridIntervals: {
      type: String,
    },

    currency: {
      type: String,
      required: true,
    },

    // style
    height: {
      type: String,
      default: '400px',
    },
  },
  data() {
    return {
      // State
      loading: false,
      chart: null,
    };
  },
  computed: {
    ...mapGetters('user', {
      currentLocale: 'getLocale',
    }),
  },

  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
  },

  methods: {
    setData() {
      // Set Data
      this.chart.data = this.data;
      // Set Series
      this.setSeries();
    },

    setSeries() {
      const series = this.createSeries();

      series.forEach(i => {
        // Set Tooltip
        this.createTooltip(this.chart, i);
      });

      // Replace all series.
      this.chart.series.setAll(series);
    },

    generateChart() {
      this.loading = true;

      /*
       * Set initial options
       */
      chartFn.initialize();
      am4core.useTheme(am4themes_animated);
      am4core.options.minPolylineStep = 5;

      /*
       * Generate chart component
       */
      this.chart = am4core.create(`${this.name}-div`, am4charts.XYChart);

      this.chart.maxZoomLevel = 1;
      this.chart.paddingTop = 50;
      this.chart.paddingRight = 20;
      this.chart.paddingLeft = 20;

      this.chart.data = [];

      /**
       * Category Axis
       */
      let categoryAxis = this.chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.name = 'categoryAxis';
      categoryAxis.dataFields.category = 'group';

      // Setting up label position, location
      categoryAxis.renderer.grid.template.disabled = true;
      categoryAxis.renderer.grid.template.location = 0.5;
      categoryAxis.renderer.labels.template.location = 0.5;

      // Setting up cell position, location
      categoryAxis.renderer.cellStartLocation = 0.2;
      categoryAxis.renderer.cellEndLocation = 0.8;
      categoryAxis.renderer.minGridDistance = 40;

      // Setting up label
      const label = categoryAxis.renderer.labels.template;
      label.truncate = true;
      label.maxWidth = 80;
      label.tooltipText = '{category}';

      // Cursor
      this.setCursor(this.chart, categoryAxis);
      categoryAxis.cursorTooltipEnabled = false;

      /**
       * Value Axis
       */
      let valueAxis = this.chart.yAxes.push(new am4charts.ValueAxis());

      valueAxis.name = 'valueAxis';
      valueAxis.renderer.grid.template.strokeOpacity = 0.2;
      valueAxis.layout = 'absolute';
      valueAxis.min = 0;

      valueAxis.cursorTooltipEnabled = false;

      // Number format
      valueAxis.numberFormatter = new am4core.NumberFormatter();
      valueAxis.numberFormatter.numberFormat = '#,###.00####a';
      valueAxis.numberFormatter.smallNumberThreshold = 0.00000000001;
      valueAxis.numberFormatter.bigNumberPrefixes = [
        { number: 1e3, suffix: this.$t('K') },
        { number: 1e6, suffix: this.$t('M') },
        { number: 1e9, suffix: this.$t('B') },
      ];

      // Title
      valueAxis.title.text = this.$t('Cost ({symbol})', { symbol: '$' });
      valueAxis.title.rotation = 0;
      valueAxis.title.align = 'center';
      valueAxis.title.valign = 'top';
      valueAxis.title.dy = -40;
      valueAxis.title.fontWeight = '700';

      // Legend
      this.chart.legend = new am4charts.Legend();

      // Set locale
      chartFn.changeLocale(this.chart, this.currentLocale);

      this.loading = false;

      return Promise.resolve();
    },

    createSeries() {
      /**
       * Set series
       */
      const seriesArray = [];

      /**
       * Series - Cost/Forecast/Previous
       */
      this.series.forEach(series => {
        // Create Series
        let seriesItem;
        seriesItem = new am4charts.ColumnSeries();
        seriesItem.name = series.name;
        seriesItem.id = series.id;

        seriesItem.dataFields.valueY = series.id;
        seriesItem.dataFields.categoryX = 'group';

        if (series.id === 'previous_cost') {
          seriesItem.fill = am4core.color(chartColors.grey);
          seriesItem.fillOpacity = 0.7;
        }

        if (series.id === 'cost') {
          seriesItem.fill = am4core.color(chartColors.indigo);
        }

        // Forecast
        if (series.id === 'forecast') {
          // seriesItem.stacked = true;
          seriesItem.stroke = am4core.color(chartColors.light_blue);
          seriesItem.strokeWidth = 2;
          seriesItem.strokeDasharray = '8,4';
          seriesItem.fill = am4core.color(chartColors.light_blue);
          seriesItem.fillOpacity = 0.2;
        }

        // Push Series
        seriesArray.push(seriesItem);
      });

      return seriesArray;
    },

    createTooltip(chart, series) {
      const options = {
        valuePrefix: '$',
        valueSuffix: '',
      };

      series.adapter.add('tooltipHTML', function() {
        let html = '<div class="__Chart-XYChart-TooltipText">{categoryX}</div>';

        chart.series.each(item => {
          const value = item.dataFields.valueY;

          html += `<div class="__Chart-XYChart-TooltipText-ListItem">
            <div class="__Chart-XYChart-TooltipText-ListItemLabel">
              <span style="color: ${item.stroke.hex}" >●</span> ${item.name}:
            </div>
            <div class="__Chart-XYChart-TooltipText-ListItemValue">
              ${options.valuePrefix}{${value}}${options.valueSuffix}
            </div>
          </div>`;
        });
        return html;
      });

      // Prevent cross-fading of tooltips
      series.tooltip.defaultState.transitionDuration = 1;
      series.tooltip.hiddenState.transitionDuration = 1;
      series.tooltip.getFillFromObject = false;
      series.tooltip.background.fill = am4core.color('#fff');
      series.tooltip.label.fill = am4core.color('#00');
      series.tooltip.numberFormatter.numberFormat = '#,###.00';
      series.tooltip.dateFormat = 'yyyy/mm';
    },

    /**
     * Cursor - Cursor
     * @param chart
     * @param xAxis
     */
    setCursor(chart, xAxis) {
      chart.cursor = new am4charts.XYCursor();
      chart.cursor.xAxis = xAxis;
      chart.cursor.fullWidthLineX = true;
      chart.cursor.lineX.strokeWidth = 0;
      chart.cursor.lineX.fill = am4core.color('#000');
      chart.cursor.lineX.fillOpacity = 0.1;
      chart.cursor.lineY.disabled = true;
      chart.cursor.maxTooltipDistance = -1;
    },
  },
});
</script>

<style lang="scss" scoped></style>
